import { useCallback, useEffect, useRef, useState } from 'react';
import appleLogo from '../../../utils/img/apple-logo.png';
import playStoreLogo from '../../../utils/img/play-store.png';

// ── Icons ───────────────────────────────────────────────────────────────────

const ArrowUpIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
    </svg>
);
const ArrowRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
);
const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
);
const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

// ── Fallback images ─────────────────────────────────────────────────────────
const FB = {
    banner: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=600&h=380&fit=crop&q=60',
    plan:   'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=600&h=450&fit=crop&q=60',
    app:    'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=400&h=700&fit=crop&q=60',
    featured:'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1000&h=400&fit=crop&q=60',
    service:'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=300&fit=crop&q=60',
};

// ── Fallback images (PlanSlider) ───────────────────────────────────────────
const FB_IMAGES = [
    'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1600&h=900&fit=crop&q=80',
    'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=1600&h=900&fit=crop&q=80',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1600&h=900&fit=crop&q=80',
    'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&h=900&fit=crop&q=80',
    'https://images.unsplash.com/photo-1554774853-aae0a22c8aa4?w=1600&h=900&fit=crop&q=80',
];

// ── Render heading with highlights ─────────────────────────────────────────
const HighlightedText = ({ text, highlight1, highlight2, color1, color2 }) => {
    if (!text) return null;
    let result = text;
    const parts = [];
    if (highlight1 && text.includes(highlight1)) {
        const idx = text.indexOf(highlight1);
        parts.push({ text: text.substring(0, idx), color: null });
        parts.push({ text: highlight1, color: color1 });
        result = text.substring(idx + highlight1.length);
    } else {
        return <>{text}</>;
    }
    if (highlight2 && result.includes(highlight2)) {
        const idx2 = result.indexOf(highlight2);
        parts.push({ text: result.substring(0, idx2), color: null });
        parts.push({ text: highlight2, color: color2 });
        parts.push({ text: result.substring(idx2 + highlight2.length), color: null });
    } else {
        parts.push({ text: result, color: null });
    }
    return <>{parts.map((p, i) => p.color ? <span key={i} style={{ color: p.color }}>{p.text}</span> : p.text)}</>;
};

// ── HeroBanner carousel ─────────────────────────────────────────────────────
const HeroBanner = ({ slides, primaryColor, onNavigateToPost, isLight }) => {
    const [selected, setSelected] = useState(0);
    const timerRef = useRef(null);
    const next = () => setSelected(s => (s + 1) % (slides?.length || 1));

    useEffect(() => {
        if (!slides || slides.length <= 1) return;
        timerRef.current = setInterval(next, 4500);
        return () => clearInterval(timerRef.current);
    }, [slides?.length]);

    if (!slides || slides.length === 0) return null;
    const slide = slides[selected];

    // Handle click - use internal navigation if postId exists, otherwise use link
    const handleClick = (e) => {
        if (slide.postId && slide.postId > 0 && onNavigateToPost) {
            e.preventDefault();
            onNavigateToPost(slide.postId);
        }
        // Otherwise let the default link behavior work
    };

    const isClickable = (slide.link && slide.link !== '#') || (slide.postId && slide.postId > 0);
    const showContent = slide.showContent !== false;
    const hasCustomCta = Object.prototype.hasOwnProperty.call(slide, 'ctaText') && slide.ctaText !== '';
    const ctaText = hasCustomCta ? slide.ctaText : (slide.postId ? 'Read Article →' : 'Read More →');
    const slideClassNames = `gmf-hero-banner-slide gmf-hero-banner-slide--active ${!showContent ? 'gmf-hero-banner-slide--no-content' : ''}`;

    return (
        <div className="gmf-hero-banner-carousel">
            {isClickable ? (
                <a
                    key={selected}
                    href={slide.link || (slide.postId ? `?post_id=${slide.postId}` : '#')}
                    onClick={handleClick}
                    target={slide.postId ? undefined : "_blank"}
                    rel={slide.postId ? undefined : "noopener noreferrer"}
                    className={`${slideClassNames} gmf-hero-banner-slide--linked`}
                    aria-label={slide.headline || 'Banner Image'}
                >
                    <img
                        src={slide.image || FB.banner}
                        alt={slide.headline || 'Banner Image'}
                        className="gmf-hero-banner-bg-img"
                        referrerPolicy="no-referrer"
                    />
                    {showContent && (
                        <div className="gmf-hero-banner-content">
                            {slide.label && <span className="gmf-hero-banner-label" style={{ background: primaryColor }}>{slide.label}</span>}
                            <h3 className="gmf-hero-banner-headline">{slide.headline}</h3>
                            <p className="gmf-hero-banner-sub">{slide.sub}</p>
                            {slide.ctaText !== '' && (
                                <span className="gmf-hero-banner-read-more">{ctaText}</span>
                            )}
                        </div>
                    )}
                </a>
            ) : (
                <div key={selected} className={slideClassNames}>
                    <img
                        src={slide.image || FB.banner}
                        alt={slide.headline || 'Banner Image'}
                        className="gmf-hero-banner-bg-img"
                        referrerPolicy="no-referrer"
                    />
                    {showContent && (
                        <div className="gmf-hero-banner-content">
                            {slide.label && <span className="gmf-hero-banner-label" style={{ background: primaryColor }}>{slide.label}</span>}
                            <h3 className="gmf-hero-banner-headline">{slide.headline}</h3>
                            <p className="gmf-hero-banner-sub">{slide.sub}</p>
                        </div>
                    )}
                </div>
            )}
            {slides.length > 1 && (
                <div className="gmf-hero-banner-dots">
                    {slides.map((_, i) => (
                        <button key={i} className="gmf-hero-banner-dot"
                            style={{ width: selected === i ? 20 : 6, background: selected === i ? primaryColor : (isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.35)') }}
                            onClick={() => setSelected(i)} aria-label={`Slide ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// ── NewsSlider ───────────────────────────────────────────────────────────────
const NewsSlider = ({ hero, primaryColor, onNavigateToPost }) => {
    const slides = hero?.newsSliderSlides || [];
    const [selected, setSelected] = useState(0);
    const [animKey, setAnimKey] = useState(0);
    const timerRef = useRef(null);
    const lockRef = useRef(false);

    const goTo = useCallback((idx) => {
        if (lockRef.current || idx === selected) return;
        lockRef.current = true;
        setSelected(idx);
        setAnimKey(k => k + 1);
        setTimeout(() => { lockRef.current = false; }, 500);
    }, [selected]);

    const goNext = useCallback(() => goTo((selected + 1) % slides.length), [selected, slides.length, goTo]);
    const goPrev = useCallback(() => goTo((selected - 1 + slides.length) % slides.length), [selected, slides.length, goTo]);

    useEffect(() => {
        if (slides.length <= 1) return;
        timerRef.current = setInterval(goNext, 5000);
        return () => clearInterval(timerRef.current);
    }, [goNext, slides.length]);

    const handleNav = (fn) => {
        clearInterval(timerRef.current);
        fn();
        timerRef.current = setInterval(goNext, 5000);
    };

    if (!slides || slides.length === 0) return null;
    const slide = slides[selected];
    const FB_NS = 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1200&h=700&fit=crop&q=80';

    // Handle click on slide content to navigate to blog post
    const handleSlideClick = () => {
        if (slide.postId && slide.postId > 0 && onNavigateToPost) {
            onNavigateToPost(slide.postId);
        } else if (slide.link || (slide.postId && slide.postId > 0)) {
            window.location.href = slide.link || `?post_id=${slide.postId}`;
        }
    };

    const isClickable = slide.link || (slide.postId && slide.postId > 0);
    const showContent = slide.showContent !== false;
    const hasCustomCta = Object.prototype.hasOwnProperty.call(slide, 'ctaText') && slide.ctaText !== '';
    const ctaText = hasCustomCta ? slide.ctaText : 'Read Article →';

    return (
        <section className="gmf-news-slider-section">
            {/* Header row */}
            <div className="gmf-ns-header">
                <div>
                    <p className="gmf-ns-eyebrow" style={{ color: primaryColor }}>News &amp; Updates</p>
                    <h2 className="gmf-ns-title">{hero.newsSliderTitle}</h2>
                </div>
                <a href={hero.newsSliderMoreLink || '#'} className="gmf-ns-more" style={{ color: primaryColor }}>
                    {hero.newsSliderMoreText} <ArrowRightIcon />
                </a>
            </div>

            {/* Main slide */}
            <div className="gmf-ns-stage">
                <div
                    key={animKey}
                    className={`gmf-ns-slide gmf-ns-slide--enter ${isClickable ? 'gmf-ns-slide--clickable' : ''} ${!showContent ? 'gmf-ns-slide--no-content' : ''}`}
                    onClick={isClickable ? handleSlideClick : undefined}
                >
                    {/* Background image */}
                    <div
                        className="gmf-ns-bg"
                        style={{ backgroundImage: `url(${slide.image || FB_NS})` }}
                    />
                    {showContent && <div className="gmf-ns-overlay" />}

                    {/* Content */}
                    {showContent && (
                        <div className="gmf-ns-content">
                            {slide.label && <span className="gmf-ns-label" style={{ background: primaryColor }}>{slide.label}</span>}
                            <h3 className="gmf-ns-headline">{slide.headline}</h3>
                            <p className="gmf-ns-sub">{slide.sub}</p>
                            <div className="gmf-ns-meta">
                                {slide.author && <span className="gmf-ns-author">{slide.author}</span>}
                                {slide.date && <><span className="gmf-ns-sep">·</span><span className="gmf-ns-date">{slide.date}</span></>}
                            </div>
                            {isClickable && slide.ctaText !== '' && (
                                <span className="gmf-ns-read-more" style={{ color: primaryColor }}>
                                    {ctaText}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Dots */}
                    <div className="gmf-ns-dots">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                className={`gmf-ns-dot ${i === selected ? 'gmf-ns-dot--active' : ''}`}
                                style={{ background: i === selected ? primaryColor : 'rgba(255,255,255,0.35)' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleNav(() => goTo(i));
                                }}
                                aria-label={`Slide ${i + 1}`}
                            />
                        ))}
                    </div>

                    {/* Nav arrows */}
                    <div className="gmf-ns-nav">
                        <button
                            className="gmf-ns-nav-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNav(goPrev);
                            }}
                            aria-label="Previous"
                        >
                            <ChevronLeftIcon />
                        </button>
                        <button
                            className="gmf-ns-nav-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNav(goNext);
                            }}
                            aria-label="Next"
                        >
                            <ChevronRightIcon />
                        </button>
                    </div>
                </div>

                {/* Thumbnail strip
                <div className="gmf-ns-thumbs">
                    {slides.map((s, i) => (
                        <button
                            key={s.id || i}
                            className={`gmf-ns-thumb ${i === selected ? 'gmf-ns-thumb--active' : ''}`}
                            onClick={() => handleNav(() => goTo(i))}
                            style={i === selected ? { borderColor: primaryColor } : {}}
                        >
                            <div
                                className="gmf-ns-thumb-img"
                                style={{ backgroundImage: `url(${s.image || FB_NS})` }}
                            />
                            <div className="gmf-ns-thumb-info">
                                <span className="gmf-ns-thumb-label" style={i === selected ? { color: primaryColor } : {}}>{s.label}</span>
                                <p className="gmf-ns-thumb-headline">{s.headline}</p>
                                {s.date && <span className="gmf-ns-thumb-date">{s.date}</span>}
                            </div>
                        </button>
                    ))}
                </div> */}
            </div>
        </section>
    );
};

// ── PlanSlider — full 100vh hero with clipPath slide transition ────────────
export const PlanSlider = ({ hero, primaryColor = '#ff0037' }) => {
    const plans = hero?.plans || [];
    const total = plans.length;

    const [current, setCurrent] = useState(0);
    const [prev, setPrev] = useState(null);
    const [flipping, setFlipping] = useState(false);
    const [direction, setDirection] = useState('next'); // 'next' | 'prev'
    const [animKey, setAnimKey] = useState(0); // forces CSS animations to restart
    const timerRef = useRef(null);
    const lockRef = useRef(false);

    // ── Navigate with slide animation ──────────────────────────────────────
    const goTo = useCallback((nextIdx, dir) => {
        if (lockRef.current || nextIdx === current) return;
        lockRef.current = true;
        setPrev(current);
        setCurrent(nextIdx);
        setDirection(dir);
        setFlipping(true);
        setAnimKey(k => k + 1);

        // After slide completes, commit the new slide (cleanup)
        setTimeout(() => {
            setFlipping(false);
            setPrev(null);
            lockRef.current = false;
        }, 800); // matches CSS animation duration
    }, [current]);

    const goNext = useCallback(() => {
        goTo((current + 1) % total, 'next');
    }, [current, total, goTo]);

    const goPrev = useCallback(() => {
        goTo((current - 1 + total) % total, 'prev');
    }, [current, total, goTo]);

    // ── Auto-advance every 5s ───────────────────────────────────────────────
    useEffect(() => {
        if (total <= 1) return;
        timerRef.current = setInterval(goNext, 5000);
        return () => clearInterval(timerRef.current);
    }, [goNext, total]);

    // Reset timer on manual nav
    const handleNav = (fn) => {
        clearInterval(timerRef.current);
        fn();
        timerRef.current = setInterval(goNext, 5000);
    };

    if (total === 0) return null;

    const currentPlan = plans[current];
    const prevPlan   = prev !== null ? plans[prev] : null;
    const bgCurrent  = currentPlan.image || FB_IMAGES[current % FB_IMAGES.length];
    const bgPrev     = prevPlan ? (prevPlan.image || FB_IMAGES[prev % FB_IMAGES.length]) : bgCurrent;

    // ── Bottom ticker: all plan subtitles/titles scrolling ─────────────────
    const tickerItems = [...plans, ...plans]; // doubled for seamless loop

    return (
        <section className="gmf-hs-section">

            {/* ── Background layers ── */}
            {/* Stable base = current slide bg (always visible underneath) */}
            <div
                className="gmf-hs-bg gmf-hs-bg--base"
                style={{ backgroundImage: `url(${bgCurrent})` }}
            />

            {/* During slide: the PREVIOUS slide's bg slides away via clipPath */}
            {flipping && prevPlan && (
                <div
                    key={`flip-${animKey}`}
                    className={`gmf-hs-bg gmf-hs-bg--flip gmf-hs-bg--flip-${direction}`}
                    style={{ backgroundImage: `url(${bgPrev})` }}
                />
            )}

            {/* Dark overlay */}
            <div className="gmf-hs-overlay" />

            {/* ── Exiting Slide content ── */}
            {flipping && prevPlan && (
                <div className={`gmf-hs-content gmf-hs-content--exit-${direction}`}>
                    <div className="gmf-hs-inner">
                        <div className="gmf-hs-text-col">
                            <span className="gmf-hs-eyebrow" style={{ color: primaryColor }}>{prevPlan.subtitle || hero?.eyebrow || ''}</span>
                            <h2 className="gmf-hs-heading">{prevPlan.title}</h2>
                            <div className="gmf-hs-meta-row">
                                <span className="gmf-hs-price" style={{ color: '#00c26d' }}>{prevPlan.price}</span>
                                {prevPlan.duration && (
                                    <>
                                        <span className="gmf-hs-sep">·</span>
                                        <span className="gmf-hs-duration">{prevPlan.duration}</span>
                                    </>
                                )}
                            </div>
                            <a href={prevPlan.ctaLink || '#'} className="gmf-hs-cta" style={{ borderColor: 'rgba(255,255,255,0.5)' }}>
                                {prevPlan.ctaText || 'Learn More'} <ArrowRightIcon />
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Entering Slide content ── */}
            <div
                key={`content-${animKey}`}
                className="gmf-hs-content gmf-hs-content--enter"
            >
                <div className="gmf-hs-inner">
                    <div className="gmf-hs-text-col">
                        <span className="gmf-hs-eyebrow" style={{ color: primaryColor }}>{currentPlan.subtitle || hero?.eyebrow || ''}</span>
                        <h2 className="gmf-hs-heading">{currentPlan.title}</h2>
                        <div className="gmf-hs-meta-row">
                            <span className="gmf-hs-price" style={{ color: '#00c26d' }}>{currentPlan.price}</span>
                            {currentPlan.duration && (
                                <>
                                    <span className="gmf-hs-sep">·</span>
                                    <span className="gmf-hs-duration">{currentPlan.duration}</span>
                                </>
                            )}
                        </div>
                        <a href={currentPlan.ctaLink || '#'} className="gmf-hs-cta" style={{ borderColor: 'rgba(255,255,255,0.5)' }}>
                            {currentPlan.ctaText || 'Learn More'} <ArrowRightIcon />
                        </a>
                    </div>
                </div>
            </div>

            {/* ── Slide counter ── */}
            <div className="gmf-hs-counter">
                <span className="gmf-hs-counter-current" style={{ color: primaryColor }}>
                    {String(current + 1).padStart(2, '0')}
                </span>
                <span className="gmf-hs-counter-sep">/</span>
                <span className="gmf-hs-counter-total">{String(total).padStart(2, '0')}</span>
            </div>

            {/* ── Progress bar ── */}
            <div className="gmf-hs-progress">
                {plans.map((_, i) => (
                    <button
                        key={i}
                        className={`gmf-hs-prog-dot ${i === current ? 'gmf-hs-prog-dot--active' : ''}`}
                        onClick={() => handleNav(() => goTo(i, i > current ? 'next' : 'prev'))}
                        aria-label={`Go to slide ${i + 1}`}
                    >
                        {i === current && (
                            <span
                                key={`fill-${animKey}`}
                                className="gmf-hs-prog-fill"
                                style={{ background: primaryColor }}
                            />
                        )}
                    </button>
                ))}
            </div>

            {/* ── Nav arrows ── */}
            <div className="gmf-hs-nav">
                <button
                    className="gmf-hs-nav-btn"
                    onClick={() => handleNav(goPrev)}
                    aria-label="Previous slide"
                >
                    <ChevronLeftIcon />
                </button>
                <button
                    className="gmf-hs-nav-btn"
                    onClick={() => handleNav(goNext)}
                    aria-label="Next slide"
                >
                    <ChevronRightIcon />
                </button>
            </div>

            {/* ── Bottom ticker ── */}
            <div className="gmf-hs-ticker">
                <div className="gmf-hs-ticker-track">
                    {tickerItems.map((plan, i) => (
                        <span key={i} className="gmf-hs-ticker-item">
                            <span
                                className="gmf-hs-ticker-dot"
                                style={{ background: i % plans.length === current ? primaryColor : 'rgba(255,255,255,0.3)' }}
                            />
                            {plan.title}
                        </span>
                    ))}
                </div>
            </div>

        </section>
    );
};

// ── Main Hero ───────────────────────────────────────────────────────────────
const Hero = ({ attributes, onNavigate }) => {
    const { hero, primaryColor = '#ff0037', colorTheme } = attributes;
    const isLight = colorTheme === 'light';
    const [activePopupIndex, setActivePopupIndex] = useState(null);

    const servicesMiniCards = hero.servicesMiniCards || [];
    const bannerSlides = hero.bannerSlides || [];

    const handleButtonClick = (e, index, btn) => {
        if (e) e.preventDefault();
        if (btn.usePopup) {
            setActivePopupIndex(index);
        } else {
            window.open(btn.link, '_blank');
        }
    };

    const confirmRedirect = () => {
        if (activePopupIndex !== null) {
            const activeBtn = (hero.heroButtons || [])[activePopupIndex];
            if (activeBtn) {
                window.open(activeBtn.link, '_blank');
            }
            setActivePopupIndex(null);
        }
    };

    const scrollToTop = () => {
        const layout = document.querySelector('.gmfSiteLayout');
        if (layout) layout.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="gmf-hero-wrapper">

            {/* ── HERO BANNER ──────────────────────────────── */}
            <section className="gmf-hero-main-section">
                <div className="gmf-hero-main-container">
                    <div className="gmf-hero-left">
                        <p className="gmf-hero-eyebrow" style={{ color: primaryColor }}>{hero.eyebrow}</p>
                        <h1 className="gmf-hero-heading">
                            <HighlightedText
                                text={hero.heading}
                                highlight1={hero.headingHighlight}
                                color1={primaryColor}
                            />
                        </h1>
                        {(hero.heroButtons || []).length > 0 && (
                            <div className="gmf-hero-action">
                                {(hero.heroButtons || []).map((btn, index) => (
                                    <a
                                        key={index}
                                        href={btn.link || '#'}
                                        className={index === 0 ? "gmf-btn-primary gmf-btn-lg" : "gmf-btn-outline gmf-btn-lg"}
                                        onClick={(e) => handleButtonClick(e, index, btn)}
                                    >
                                {btn.text}
                                    </a>
                                ))}
                            </div>
                        )}


                    </div>

                    <HeroBanner slides={bannerSlides} primaryColor={primaryColor} onNavigateToPost={onNavigate} isLight={isLight} />

                </div>
            </section>

            {/* ── PLAN SLIDER ──────────────────────────────── */}
            <PlanSlider hero={hero} primaryColor={primaryColor} />

            {/* ── APP SECTION ──────────────────────────────── */}
            <section className="gmf-app-section">
                <div className="gmf-app-content-left">
                    <h2 className="gmf-app-title">{hero.appSectionTitle}</h2>
                    <p className="gmf-app-subtitle" style={{ color: '#00c26d' }}>{hero.appSectionSubtitle}</p>

                    <div className="gmf-app-store-btns">
                        <a href={hero.playStoreLink || '#'} className={`gmf-app-store-btn ${hero.playStoreImage ? 'has-img' : ''}`} target="_blank" rel="noopener noreferrer">
                            {hero.playStoreImage ? (
                                <img src={hero.playStoreImage} alt={hero.playStoreText || "Google Play"} className="gmf-app-badge-img" />
                            ) : (
                                <>
                                    <img src={hero.playStoreIcon || playStoreLogo} alt="" style={{width: 24, height: 24, objectFit: 'contain'}} />
                                    {hero.playStoreText || 'Google Play'}
                                </>
                            )}
                        </a>
                        <a href={hero.appStoreLink || '#'} className={`gmf-app-store-btn ${hero.appStoreImage ? 'has-img' : ''}`} target="_blank" rel="noopener noreferrer">
                            {hero.appStoreImage ? (
                                <img src={hero.appStoreImage} alt={hero.appStoreText || "App Store"} className="gmf-app-badge-img" />
                            ) : (
                                <>
                                    <img src={hero.appStoreIcon || appleLogo} alt="" style={{width: 28, height: 28, objectFit: 'contain', paddingBottom: 5}} />
                                    {hero.appStoreText || 'App Store'}
                                </>
                            )}
                        </a>
                    </div>
                </div>

                <div className="gmf-app-phone-wrap">
                    <img
                        src={hero.appImage || FB.app}
                        alt={hero.appBadgeText}
                        className="gmf-app-phone-img"
                        referrerPolicy="no-referrer"
                    />
                    <div className="gmf-app-phone-badge" style={{ background: primaryColor }}>{hero.appBadgeText}</div>
                </div>
            </section>

            {/* ── FEATURED BANNER ──────────────────────────── */}
            <section className="gmf-featured-section">
                <p className="gmf-featured-label">{hero.featuredLabel}</p>
                <h2 className="gmf-featured-title">
                    <HighlightedText
                        text={hero.featuredTitle}
                        highlight1={hero.featuredTitleHighlight1}
                        highlight2={hero.featuredTitleHighlight2}
                        color1={primaryColor}
                        color2="#00c26d"
                    />
                </h2>
                <div className="gmf-featured-card">
                    <img
                        src={hero.featuredImage || FB.featured}
                        alt={hero.featuredLabel}
                        className="gmf-featured-img"
                        referrerPolicy="no-referrer"
                    />
                    <div className="gmf-featured-overlay">
                        <p className="gmf-featured-overlay-text">{hero.featuredDescription}</p>
                        <a
                            href={hero.featuredBtnLink || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="gmf-featured-cta-btn"
                            style={{ background: primaryColor }}
                        >
                            {hero.featuredBtnText} <ArrowRightIcon />
                        </a>
                    </div>
                </div>
            </section>

            {/* ── SERVICES MINI GRID ────────────────────────── */}
            <section className="gmf-services-mini-section">
                <div className="gmf-services-mini-header">
                    <h2 className="gmf-services-mini-title">{hero.servicesMiniTitle}</h2>
                    <a href={hero.servicesMiniMoreLink || '#'} className="gmf-services-mini-more" style={{ color: primaryColor }}>
                        {hero.servicesMiniMoreText} <ArrowRightIcon />
                    </a>
                </div>
                <div className="gmf-services-mini-grid">
                    {servicesMiniCards.map((g, i) => (
                        <div key={g.id || i} className="gmf-service-mini-card">
                            <div className="gmf-service-mini-top">
                                <p className="gmf-service-mini-name">{g.name}</p>
                                {g.icon && <img src={g.icon} alt="icon" className="gmf-service-mini-icon" referrerPolicy="no-referrer" />}
                                {g.desc && <p className="gmf-service-mini-desc">{g.desc}</p>}
                                {g.price && <p className="gmf-service-mini-price" style={{ color: '#00c26d' }}>{g.price}</p>}
                                <span className="gmf-service-mini-number">{g.number || (i + 1)}</span>
                            </div>
                            <div className="gmf-service-mini-bottom">
                                <img src={g.image || FB.service} alt={g.name} className="gmf-service-mini-img" referrerPolicy="no-referrer" />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── NEWS SLIDER ────────────────────────────── */}
            <NewsSlider hero={hero} primaryColor={primaryColor} onNavigateToPost={onNavigate} />

            {/* ── DYNAMIC FLOATING ACTION BUTTONS ───────── */}
            {(hero.floatingButtons || []).map((btn, index) => {
                // Calculate position: stack above WhatsApp with 64px spacing (48px height + 16px gap)
                // Desktop: WhatsApp at 96px, Mobile: WhatsApp at 84px
                const buttonCount = (hero.floatingButtons || []).length;
                const reverseIndex = buttonCount - 1 - index;
                const desktopBottom = 96 + ((reverseIndex + 1) * 64);
                const mobileBottom = 84 + ((reverseIndex + 1) * 60);

                return (
                    <a
                        key={btn.id}
                        href={btn.url || '#'}
                        target={btn.openInNewTab !== false ? '_blank' : '_self'}
                        rel={btn.openInNewTab !== false ? 'noopener noreferrer' : undefined}
                        className={`gmf-floating-btn gmf-floating-btn--pos-${reverseIndex} ${btn.showLabel ? 'gmf-floating-btn--with-label' : ''}`}
                        aria-label={btn.label}
                        style={{
                            backgroundColor: btn.bgColor || primaryColor,
                            color: btn.textColor || '#ffffff',
                            '--fb-desktop-bottom': `${desktopBottom}px`,
                            '--fb-mobile-bottom': `${mobileBottom}px`,
                        }}
                        data-label={btn.label}
                    >
                        {btn.icon ? (
                            <img src={btn.icon} alt="" className="gmf-floating-btn-icon" />
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="16"/>
                                <line x1="8" y1="12" x2="16" y2="12"/>
                            </svg>
                        )}
                        {btn.showLabel && <span className="gmf-floating-btn-text">{btn.label}</span>}
                    </a>
                );
            })}

            {/* Floating WhatsApp */}
            {hero.whatsappShow && hero.whatsappNumber && (() => {
                const cleanNum = hero.whatsappNumber.replace(/[^0-9]/g, '');
                const waUrl = `https://wa.me/${cleanNum}${hero.whatsappMessage ? '?text=' + encodeURIComponent(hero.whatsappMessage) : ''}`;
                return (
                    <a href={waUrl} target="_blank" rel="noopener noreferrer" className="gmf-whatsapp-btn" aria-label="Chat on WhatsApp">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="currentColor">
                            <path d="M16.004 0C7.165 0 .003 7.16.003 15.997a15.94 15.94 0 0 0 2.46 8.554L.015 32l7.702-2.39A15.94 15.94 0 0 0 16.004 32C24.838 32 32 24.836 32 15.997 32 7.16 24.838 0 16.004 0zm0 29.39a13.35 13.35 0 0 1-7.222-2.116l-.518-.307-4.57 1.42 1.45-4.44-.34-.54A13.33 13.33 0 0 1 2.63 15.997c0-7.38 6.003-13.38 13.374-13.38 7.374 0 13.38 6 13.38 13.38 0 7.377-6.006 13.393-13.38 13.393zm7.34-10.016c-.403-.2-2.383-1.175-2.752-1.31-.37-.133-.638-.2-.907.2-.268.4-1.04 1.31-1.275 1.578-.235.268-.47.302-.873.1-.403-.2-1.7-.626-3.24-1.997-1.197-1.068-2.005-2.387-2.24-2.79-.234-.402-.025-.619.176-.82.18-.18.403-.468.604-.703.2-.234.268-.4.403-.668.134-.268.067-.502-.034-.703-.1-.2-.907-2.185-1.243-2.992-.327-.785-.66-.678-.907-.69l-.772-.014a1.48 1.48 0 0 0-1.074.502c-.37.402-1.41 1.377-1.41 3.36 0 1.984 1.443 3.9 1.644 4.168.2.268 2.84 4.333 6.882 6.074.96.415 1.712.662 2.297.848.965.306 1.843.263 2.537.16.774-.116 2.383-.975 2.72-1.916.335-.94.335-1.746.234-1.916-.1-.168-.37-.268-.773-.468z"/>
                        </svg>
                    </a>
                );
            })()}

            {/* Floating scroll-to-top */}
            <button className="gmf-scroll-top" onClick={scrollToTop} aria-label="Back to top" style={{ background: primaryColor }}>
                <ArrowUpIcon />
            </button>

            {/* ── Confirmation Popup Modal ── */}
            {activePopupIndex !== null && (() => {
                const activeBtn = (hero.heroButtons || [])[activePopupIndex];
                if (!activeBtn) return null;
                return (
                    <div className="gmf-modal-overlay">
                        <div className="gmf-modal-container">
                            <div className="gmf-modal-content">
                                <h3 className="gmf-modal-title">{activeBtn.popupTitle}</h3>
                                <p className="gmf-modal-desc">{activeBtn.popupDesc}</p>
                                <div className="gmf-modal-actions">
                                    <button
                                        className="gmf-modal-btn gmf-modal-btn--cancel"
                                        onClick={() => setActivePopupIndex(null)}
                                    >
                                        {activeBtn.popupBtnCancel}
                                    </button>
                                    <button
                                        className="gmf-modal-btn gmf-modal-btn--go gmf-accent-bg"
                                        onClick={confirmRedirect}
                                    >
                                        {activeBtn.popupBtnGo}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default Hero;
